package com.example.positionahead.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.example.positionahead.data.*
import com.example.positionahead.domain.*
import com.example.positionahead.model.*
import com.russhwolf.settings.Settings
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlin.random.Random

data class PortfolioUiState(
    val summary: PortfolioSummary = PortfolioSummary(
        totalMarketValue = 0.0,
        totalCostBasis = 0.0,
        totalUnrealizedGainLoss = 0.0,
        totalUnrealizedGainLossPercent = 0.0,
        estimatedAnnualIncome = 0.0,
        stockAllocationPercent = 0.0,
        bondAllocationPercent = 0.0,
        cashAllocationPercent = 0.0,
        positions = emptyList(),
    ),
    val recommendations: List<Recommendation> = emptyList(),
    val goal: FinancialGoal = FinancialGoal(),
    val quotes: Map<String, Quote> = emptyMap(),
    val portfolios: List<SavedPortfolio> = emptyList(),
    val activePortfolioId: String = PortfolioRepository.DEFAULT_PORTFOLIO_ID,
    val activePortfolioName: String = "My Portfolio",
    val activeAccountColor: String = com.example.positionahead.model.AccountColors.DEFAULT,
    val activeAccountStartDate: String? = null,
    val isLoading: Boolean = false,
    val isRefreshing: Boolean = false,
    val lastUpdated: String = "",
    val errorMessage: String? = null,
    val usesLiveQuotes: Boolean = false,
    val apiKey: String = "",
    val importErrors: List<String> = emptyList(),
    val importSuccessCount: Int = 0,
    val existingSymbols: List<String> = emptyList(),
    val analyticsWidgetOrder: List<AnalyticsWidgetType> = AnalyticsWidgetType.defaultOrder,
    val isEditingAnalyticsLayout: Boolean = false,
    val discoverSections: List<DiscoverSection> = emptyList(),
    val watchlist: List<WatchlistEntry> = emptyList(),
    val alerts: List<PriceAlert> = emptyList(),
    val triggeredAlerts: List<PriceAlert> = emptyList(),
    val portfolioHistory: PortfolioPriceHistory = PortfolioPriceHistory(emptyList(), null),
    val symbolHistories: Map<String, SymbolPriceHistory> = emptyMap(),
    val chartSymbol: String = "QQQM",
    val chartRangeDays: Int = 90,
    val chartHistory: SymbolPriceHistory? = null,
    val chartQuote: Quote? = null,
    val chartPerformance: SymbolPerformance? = null,
    val chartSymbolSuggestions: List<String> = emptyList(),
    val livePricedHoldingsCount: Int = 0,
    val totalHoldingsCount: Int = 0,
    val targetHoldings: List<TargetHolding> = emptyList(),
    val rebalanceTrades: List<RebalanceTrade> = emptyList(),
    val portfolioProjections: List<PortfolioProjectionPoint> = emptyList(),
    val portfolioDeltas: List<Pair<String, Double>> = emptyList(),
    val afterPlanSummary: PortfolioSummary? = null,
    val planComparePortfolioId: String? = null,
    val comparePortfolioSummary: PortfolioSummary? = null,
    val comparePortfolioName: String? = null,
    val goalTargetSummary: PortfolioSummary? = null,
    val projectionCompareLabel: String = "",
    val currentAnnualIncomeEstimate: Double = 0.0,
    val compareAnnualIncomeEstimate: Double = 0.0,
    val suggestedPortfolioSummary: PortfolioSummary? = null,
    val suggestedPortfolioDescription: String = "",
    val suggestedDiscoverAdds: List<DiscoverIdea> = emptyList(),
    val planComparison: PlanComparisonVerdict? = null,
    val planCompareOptions: List<PlanCompareOption> = emptyList(),
    val planSlotBId: String? = null,
    val planSlotCId: String? = null,
    val homeState: HomeState? = null,
    val portfolioHealth: PortfolioHealth? = null,
    val positionAnalyses: Map<String, PositionAnalysis> = emptyMap(),
    val capitalScenarios: List<CapitalScenario> = emptyList(),
    val availableCapital: Double = 500.0,
)

data class PlanTradePrefill(
    val symbol: String,
    val dollarAmount: Double,
)

class PortfolioViewModel(
    settings: Settings = createAppSettings(),
    private val repository: PortfolioRepository = PortfolioRepository(settings),
    private val analyticsLayoutRepository: AnalyticsLayoutRepository = AnalyticsLayoutRepository(settings),
    private val alertRepository: AlertRepository = AlertRepository(settings),
    private val quoteService: StockQuoteService = StockQuoteService(createHttpClient(), settings),
) : ViewModel() {

    private val _uiState = MutableStateFlow(PortfolioUiState())
    val uiState: StateFlow<PortfolioUiState> = _uiState.asStateFlow()

    private var refreshJob: Job? = null
    private var catalogRefreshCounter: Int = 0

    init {
        repository.seedSampleDataIfEmpty()
        _uiState.update {
            it.copy(
                portfolios = repository.getPortfolios(),
                activePortfolioId = repository.getActivePortfolioId(),
                activePortfolioName = repository.getActivePortfolio().name,
                goal = repository.getGoal(),
                apiKey = quoteService.apiKey,
                usesLiveQuotes = quoteService.usesLiveQuotes,
                analyticsWidgetOrder = analyticsLayoutRepository.getWidgetOrder(),
            )
        }
        refreshQuotes(startPolling = true)
    }

    fun refreshQuotes(startPolling: Boolean = false) {
        refreshJob?.cancel()
        refreshJob = viewModelScope.launch {
            loadPortfolioData()

            if (startPolling) {
                while (isActive) {
                    delay(POLL_INTERVAL_MS)
                    if (!isActive) break
                    loadPortfolioData()
                }
            }
        }
    }

    private suspend fun loadPortfolioData() {
        _uiState.update { it.copy(isRefreshing = true, errorMessage = null) }
        try {
            val active = repository.getActivePortfolio()
            val positions = active.positions
            val positionSymbols = positions.map { it.symbol.uppercase() }
            val watchlistSymbols = active.watchlist.map { it.symbol.uppercase() }
            val alertSymbols = alertRepository.getAlerts().map { it.symbol.uppercase() }
            val chartSymbol = _uiState.value.chartSymbol.uppercase().ifBlank { "QQQM" }
            val portfolioSymbols = (
                positionSymbols +
                    watchlistSymbols +
                    alertSymbols +
                    listOf(chartSymbol)
                ).distinct()
            val portfolioQuotes = quoteService.fetchQuotes(
                portfolioSymbols,
                allowEstimatedFallback = true,
            )
            catalogRefreshCounter += 1
            val catalogSymbols = MarketSuggestionCatalog.allSymbols()
            val shouldRefreshCatalog = !quoteService.usesLiveQuotes ||
                catalogRefreshCounter % CATALOG_REFRESH_EVERY == 0
            val catalogQuotes = if (shouldRefreshCatalog) {
                quoteService.fetchQuotes(catalogSymbols, allowEstimatedFallback = true)
            } else {
                _uiState.value.quotes.filterKeys { it in catalogSymbols }
            }
            val quotes = portfolioQuotes + catalogQuotes.filterKeys { it !in portfolioQuotes }
            val performanceSymbols = (portfolioSymbols + catalogSymbols).distinct()
            val performance = quoteService.fetchPerformance(performanceSymbols, quotes)
            var summary = PortfolioCalculator.buildSummary(positions, quotes)
            summary = PerformanceCalculator.applyPerformance(summary, performance)
            val positionHistorySymbols = positionSymbols.distinct()
            val chartRangeDays = _uiState.value.chartRangeDays
            val historySymbols = (positionHistorySymbols + chartSymbol).distinct()
            val symbolHistories = quoteService.fetchPriceHistory(historySymbols, days = 30)
            val chartHistories = quoteService.fetchPriceHistory(listOf(chartSymbol), days = chartRangeDays)
            val portfolioHistory = PortfolioHistoryCalculator.buildPortfolioHistory(positions, symbolHistories)
            val targetHoldings = RebalancePlanner.buildTargetHoldings(summary, active.goal)
            val portfolioDeltas = RebalancePlanner.deltaFromCurrent(summary, targetHoldings)
            val rebalanceTrades = RebalancePlanner.computeTrades(summary, active.goal, quotes)
            val afterPlanSummary = PlanSimulator.summarizeAfterPlan(
                positions = positions,
                trades = rebalanceTrades,
                quotes = quotes,
            )
            val comparePortfolio = _uiState.value.planComparePortfolioId?.let { id ->
                repository.getPortfolios().find { it.id == id && it.id != active.id }
            }
            val comparePortfolioSummary = comparePortfolio?.let {
                PortfolioCalculator.buildSummary(it.positions, quotes)
            }
            val goalTargetPositions = GoalTargetPortfolioBuilder.positionsFromTargets(targetHoldings, quotes)
            val goalTargetSummary = PortfolioCalculator.buildSummary(goalTargetPositions, quotes)
            val discoverSections = DiscoverAnalyzer.analyze(
                ownedSymbols = positionSymbols.toSet(),
                goal = active.goal,
                quotes = quotes,
                performance = performance,
            )
            val planCompareOptions = PlanStrategyBuilder.buildCompareOptions(
                currentPositions = positions,
                currentSummary = summary,
                goal = active.goal,
                targetHoldings = targetHoldings,
                trades = rebalanceTrades,
                discoverSections = discoverSections,
                quotes = quotes,
                performance = performance,
                savedPortfolios = repository.getPortfolios(),
                activePortfolioId = active.id,
            )
            val previousPlan = _uiState.value
            val slotBId = previousPlan.planSlotBId?.takeIf { id -> planCompareOptions.any { it.id == id } }
                ?: planCompareOptions.getOrNull(0)?.id
            val slotCId = previousPlan.planSlotCId?.takeIf { id ->
                planCompareOptions.any { it.id == id } && id != slotBId
            } ?: planCompareOptions.firstOrNull { it.id != slotBId }?.id
            val slotBOption = planCompareOptions.find { it.id == slotBId }
            val suggestedBuild = SuggestedPortfolioBuilder.build(
                currentPositions = positions,
                summary = summary,
                goal = active.goal,
                targetHoldings = targetHoldings,
                trades = emptyList(),
                discoverSections = discoverSections,
                quotes = quotes,
            )
            val suggestedSummary = PortfolioCalculator.buildSummary(suggestedBuild.positions, quotes)
            val projectionCompareSummary = slotBOption?.summary
                ?: planCompareOptions.firstOrNull()?.summary
            val projectionCompareLabel = slotBOption?.title ?: "Strategy"
            val portfolioProjections = if (projectionCompareSummary != null && summary.totalMarketValue > 0) {
                PortfolioProjection.comparePortfolios(
                    current = summary,
                    target = projectionCompareSummary,
                    quotes = quotes,
                    performance = performance,
                    monthlyContribution = active.goal.monthlyContribution,
                )
            } else {
                emptyList()
            }
            val planComparison = if (slotBOption != null && summary.totalMarketValue > 0) {
                PlanPortfolioComparer.compare(
                    current = summary,
                    suggested = slotBOption.summary,
                    goal = active.goal,
                    projections = portfolioProjections,
                )
            } else {
                null
            }
            val compareIncome = projectionCompareSummary?.estimatedAnnualIncome ?: 0.0
            val recommendations = PortfolioAnalyzer.analyze(summary, active.goal, quotes)
            val livePriced = summary.positions.count { it.hasLivePrice }
            val evaluatedAlerts = AlertEvaluator.evaluate(
                alertRepository.getAlerts(),
                quotes,
                summary.positions,
            )
            alertRepository.saveAlerts(evaluatedAlerts)
            val watchlist = active.watchlist.map { item ->
                val symbol = item.symbol.uppercase()
                WatchlistEntry(
                    item = item,
                    quote = quotes[symbol],
                    performance = performance[symbol],
                )
            }

            // ADVANCED ANALYSIS
            val health = PortfolioHealthAnalyzer.analyze(summary, active.goal)
            val positionAnalyses = summary.positions.associate { 
                it.position.symbol.uppercase() to PositionDetailAnalyzer.analyze(it, active.goal)
            }
            val capitalScenarios = CapitalAllocationSimulator.generateScenarios(
                _uiState.value.availableCapital, 
                summary, 
                active.goal
            )
            
            // Daily Attention logic
            val attentionItems = mutableListOf<AttentionItem>()
            positionAnalyses.values.forEach { analysis ->
                if (analysis.signal == PositionSignal.WATCH || analysis.signal == PositionSignal.REVIEW) {
                    attentionItems.add(AttentionItem(
                        title = analysis.symbol,
                        detail = analysis.thingsToWatch.firstOrNull() ?: "Needs review",
                        symbol = analysis.symbol,
                        type = if (analysis.signal == PositionSignal.REVIEW) AttentionType.WARNING else AttentionType.INFO
                    ))
                }
            }
            if (summary.totalUnrealizedGainLossPercent > 5) {
                attentionItems.add(AttentionItem("Portfolio Performance", "Your portfolio is up ${summary.totalUnrealizedGainLossPercent.toInt()}% all-time.", type = AttentionType.SUCCESS))
            }
            
            val totalDayChange = summary.positions.sumOf { snap ->
                val quote = quotes[snap.position.symbol.uppercase()]
                if (quote != null) {
                    val pricePrev = quote.price / (1 + quote.changePercent / 100.0)
                    val diff = quote.price - pricePrev
                    diff * snap.position.lots.sumOf { it.shares }
                } else 0.0
            }
            val totalDayChangePercent = if (summary.totalMarketValue > 0) {
                (totalDayChange / (summary.totalMarketValue - totalDayChange)) * 100.0
            } else 0.0

            val homeState = HomeState(
                portfolioValue = summary.totalMarketValue,
                dayChange = totalDayChange,
                dayChangePercent = totalDayChangePercent,
                attentionItems = attentionItems.take(3),
                opportunities = recommendations.filter { it.action == RecommendationAction.BUY }.take(2),
                healthScore = health.score
            )

            _uiState.update {
                it.copy(
                    summary = summary,
                    recommendations = recommendations,
                    discoverSections = discoverSections,
                    watchlist = watchlist,
                    alerts = evaluatedAlerts,
                    triggeredAlerts = evaluatedAlerts.filter { alert -> alert.triggeredAt != null && !alert.read },
                    portfolioHistory = portfolioHistory,
                    symbolHistories = symbolHistories,
                    chartHistory = chartHistories[chartSymbol],
                    chartQuote = quotes[chartSymbol],
                    chartPerformance = performance[chartSymbol],
                    chartSymbolSuggestions = (positionSymbols + watchlistSymbols).distinct().sorted(),
                    chartSymbol = chartSymbol,
                    goal = active.goal,
                    quotes = quotes,
                    portfolios = repository.getPortfolios(),
                    activePortfolioId = active.id,
                    activePortfolioName = active.name,
                    activeAccountColor = active.colorHex,
                    activeAccountStartDate = active.startDate,
                    existingSymbols = positionSymbols.distinct().sorted(),
                    isLoading = false,
                    isRefreshing = false,
                    lastUpdated = "Updated just now",
                    usesLiveQuotes = quoteService.usesLiveQuotes,
                    livePricedHoldingsCount = livePriced,
                    totalHoldingsCount = summary.positions.size,
                    targetHoldings = targetHoldings,
                    rebalanceTrades = rebalanceTrades,
                    portfolioProjections = portfolioProjections,
                    portfolioDeltas = portfolioDeltas,
                    afterPlanSummary = afterPlanSummary,
                    comparePortfolioSummary = comparePortfolioSummary,
                    comparePortfolioName = comparePortfolio?.name,
                    goalTargetSummary = goalTargetSummary.takeIf { it.totalMarketValue > 0 },
                    projectionCompareLabel = projectionCompareLabel,
                    currentAnnualIncomeEstimate = summary.estimatedAnnualIncome,
                    compareAnnualIncomeEstimate = compareIncome,
                    suggestedPortfolioSummary = suggestedSummary.takeIf { it.totalMarketValue > 0 },
                    suggestedPortfolioDescription = suggestedBuild.description,
                    suggestedDiscoverAdds = suggestedBuild.addedFromDiscover,
                    planComparison = planComparison,
                    planCompareOptions = planCompareOptions,
                    planSlotBId = slotBId,
                    planSlotCId = slotCId,
                    homeState = homeState,
                    portfolioHealth = health,
                    positionAnalyses = positionAnalyses,
                    capitalScenarios = capitalScenarios,
                    errorMessage = null,
                )
            }
        } catch (e: Exception) {
            _uiState.update {
                it.copy(
                    isLoading = false,
                    isRefreshing = false,
                    errorMessage = e.message ?: "Failed to fetch quotes",
                )
            }
        }
    }

    fun selectPortfolio(id: String) {
        repository.setActivePortfolio(id)
        refreshQuotes()
    }

    fun createAccount(
        name: String,
        copyFromPortfolioId: String?,
        colorHex: String,
        startDate: String?,
        notes: String,
    ) {
        repository.createPortfolio(name, copyFromPortfolioId, colorHex, startDate, notes)
        refreshQuotes()
    }

    fun updateAccount(
        id: String,
        name: String,
        colorHex: String,
        startDate: String?,
        notes: String,
    ) {
        repository.updatePortfolioDetails(id, name, colorHex, startDate, notes)
        _uiState.update {
            val active = repository.getActivePortfolio()
            it.copy(
                portfolios = repository.getPortfolios(),
                activePortfolioName = active.name,
                activeAccountColor = active.colorHex,
                activeAccountStartDate = active.startDate,
            )
        }
    }

    fun deleteAccount(id: String) {
        repository.deletePortfolio(id)
        refreshQuotes()
    }

    fun addPurchase(
        symbol: String,
        shares: Double,
        costBasis: Double,
        assetClass: AssetClass,
        purchaseDate: String? = null,
        note: String? = null,
        portfolioId: String? = null,
    ) {
        val upper = symbol.trim().uppercase()
        val targetPortfolioId = portfolioId ?: repository.getActivePortfolioId()
        val portfolio = repository.getPortfolios().find { it.id == targetPortfolioId } ?: return
        val existing = portfolio.positions.find { it.symbol.uppercase() == upper }
        
        if (existing != null) {
            repository.addLot(existing.id, shares, costBasis, purchaseDate, note, portfolioId = targetPortfolioId)
        } else {
            repository.addPosition(
                positionWithSingleLot(
                    id = Random.nextLong().toString(16),
                    symbol = upper,
                    shares = shares,
                    costBasisPerShare = costBasis,
                    assetClass = assetClass,
                    purchaseDate = purchaseDate,
                    note = note,
                ),
                portfolioId = targetPortfolioId
            )
        }
        refreshQuotes()
    }

    fun addLot(
        positionId: String,
        shares: Double,
        costBasisPerShare: Double,
        purchaseDate: String? = null,
        note: String? = null,
    ) {
        repository.addLot(positionId, shares, costBasisPerShare, purchaseDate, note)
        refreshQuotes()
    }

    fun updateLot(
        positionId: String,
        lotId: String,
        shares: Double,
        costBasisPerShare: Double,
        purchaseDate: String?,
        note: String?,
    ) {
        repository.updateLot(positionId, lotId, shares, costBasisPerShare, purchaseDate, note)
        refreshQuotes()
    }

    fun sellLot(positionId: String, lotId: String, sharesToSell: Double) {
        repository.sellLot(positionId, lotId, sharesToSell)
        refreshQuotes()
    }

    fun removePosition(id: String) {
        repository.removePosition(id)
        refreshQuotes()
    }

    fun importCsv(csv: String, replaceExisting: Boolean = false) {
        val result = CsvImporter.parse(csv)
        if (result.positions.isNotEmpty()) {
            val positions = if (replaceExisting) {
                result.positions
            } else {
                PositionMerger.mergeIntoExisting(repository.getPositions(), result.positions)
            }
            repository.savePositions(positions)
        }
        _uiState.update {
            it.copy(
                importErrors = result.errors,
                importSuccessCount = result.positions.sumOf { position -> position.lots.size },
            )
        }
        refreshQuotes()
    }

    fun createTheoreticalPortfolio(name: String) {
        repository.createTheoreticalPortfolio(name)
        refreshQuotes()
    }

    fun setPlanComparePortfolio(portfolioId: String?) {
        _uiState.update { it.copy(planComparePortfolioId = portfolioId) }
        refreshQuotes()
    }

    /** Column 2 or 3 strategy picker on Plan tab. */
    fun setPlanCompareSlot(columnIndex: Int, optionId: String) {
        _uiState.update { state ->
            when (columnIndex) {
                2 -> state.copy(planSlotBId = optionId)
                3 -> state.copy(
                    planSlotCId = optionId,
                    planSlotBId = state.planSlotBId ?: optionId,
                )
                else -> state
            }
        }
        refreshQuotes()
    }

    fun savePlanSlotAsPortfolio(columnIndex: Int) {
        val state = _uiState.value
        val optionId = when (columnIndex) {
            2 -> state.planSlotBId
            3 -> state.planSlotCId
            else -> return
        }
        val option = state.planCompareOptions.find { it.id == optionId } ?: return
        savePositionsAsTheoretical(option.title, option.summary.positions.map { it.position })
    }

    fun createGoalTargetPortfolio(name: String = "Suggested portfolio") {
        savePlanSlotAsPortfolio(2)
    }

    private fun savePositionsAsTheoretical(name: String, positions: List<Position>) {
        if (positions.isEmpty()) return
        val active = repository.getActivePortfolio()
        val previousActiveId = repository.getActivePortfolioId()
        val created = repository.createPortfolio(
            name = name.trim().ifBlank { "Goal target" },
            copyFromPortfolioId = active.id,
            isTheoretical = true,
        )
        repository.setPortfolioPositions(created.id, positions)
        repository.setActivePortfolio(previousActiveId)
        _uiState.update { it.copy(planComparePortfolioId = created.id) }
        refreshQuotes()
    }

    fun savePlanAsTheoreticalPortfolio(name: String = "After plan (simulated)") {
        val active = repository.getActivePortfolio()
        val state = _uiState.value
        if (state.rebalanceTrades.isEmpty()) {
            createGoalTargetPortfolio(name.ifBlank { "Goal target" })
            return
        }
        val simulated = PlanSimulator.applyTrades(
            positions = active.positions,
            trades = state.rebalanceTrades,
            quotes = state.quotes,
        )
        val previousActiveId = repository.getActivePortfolioId()
        val created = repository.createPortfolio(
            name = name.trim().ifBlank { "After plan (simulated)" },
            copyFromPortfolioId = active.id,
            isTheoretical = true,
        )
        repository.setPortfolioPositions(created.id, simulated)
        repository.setActivePortfolio(previousActiveId)
        _uiState.update { it.copy(planComparePortfolioId = created.id) }
        refreshQuotes()
    }

    fun selectChartSymbol(symbol: String) {
        _uiState.update { it.copy(chartSymbol = symbol.uppercase()) }
        refreshQuotes()
    }

    fun selectChartRange(days: Int) {
        _uiState.update { it.copy(chartRangeDays = days) }
        refreshQuotes()
    }

    fun updateGoal(
        plannerGoalType: PlannerGoalType,
        customGoalLabel: String?,
        targetAmount: Double,
        targetDateYears: Int,
        monthlyContribution: Double,
        age: Int,
        riskTolerance: RiskTolerance,
        objective: PortfolioObjective,
        maxSinglePositionPercent: Double,
        targetYieldPercent: Double,
        investmentHorizonYears: Int,
    ) {
        val goal = FinancialGoal(
            plannerGoalType = plannerGoalType,
            customGoalLabel = customGoalLabel,
            targetAmount = targetAmount,
            targetDateYears = targetDateYears,
            monthlyContribution = monthlyContribution,
            age = age,
            riskTolerance = riskTolerance,
            objective = objective,
            maxSinglePositionPercent = maxSinglePositionPercent,
            targetYieldPercent = targetYieldPercent,
            investmentHorizonYears = investmentHorizonYears,
        )
        repository.saveGoal(goal)
        refreshQuotes()
    }

    fun refreshRecommendations() {
        recomputeRecommendations(repository.getGoal())
    }

    private fun recomputeRecommendations(goal: FinancialGoal) {
        val summary = _uiState.value.summary
        val quotes = _uiState.value.quotes
        val owned = summary.positions.map { it.position.symbol.uppercase() }.toSet()
        val recommendations = PortfolioAnalyzer.analyze(summary, goal, quotes)
        val performance = summary.positions.associate { snapshot ->
            snapshot.position.symbol.uppercase() to SymbolPerformance(
                return1MonthPercent = snapshot.return1MonthPercent,
                returnYtdPercent = snapshot.returnYtdPercent,
            )
        }
        val discoverSections = DiscoverAnalyzer.analyze(
            ownedSymbols = owned,
            goal = goal,
            quotes = quotes,
            performance = performance,
        )
        _uiState.update {
            it.copy(goal = goal, recommendations = recommendations, discoverSections = discoverSections)
        }
    }

    fun saveApiKey(key: String) {
        quoteService.apiKey = key
        _uiState.update { it.copy(apiKey = key, usesLiveQuotes = quoteService.usesLiveQuotes) }
        refreshQuotes()
    }

    fun clearImportStatus() {
        _uiState.update { it.copy(importErrors = emptyList(), importSuccessCount = 0) }
    }

    fun lookupQuote(symbol: String) {
        val upper = symbol.trim().uppercase()
        if (upper.isBlank()) return
        viewModelScope.launch {
            runCatching {
                quoteService.fetchQuotes(
                    listOf(upper),
                    allowEstimatedFallback = !quoteService.usesLiveQuotes,
                )
            }.onSuccess { fetched ->
                if (fetched.isNotEmpty()) {
                    _uiState.update { it.copy(quotes = it.quotes + fetched) }
                }
            }
        }
    }

    fun toggleAnalyticsLayoutEditing() {
        _uiState.update { it.copy(isEditingAnalyticsLayout = !it.isEditingAnalyticsLayout) }
    }

    fun moveAnalyticsWidgetUp(index: Int) {
        if (index <= 0) return
        updateAnalyticsWidgetOrder(swap(_uiState.value.analyticsWidgetOrder, index, index - 1))
    }

    fun moveAnalyticsWidgetDown(index: Int) {
        val order = _uiState.value.analyticsWidgetOrder
        if (index >= order.lastIndex) return
        updateAnalyticsWidgetOrder(swap(order, index, index + 1))
    }

    fun resetAnalyticsLayout() {
        updateAnalyticsWidgetOrder(analyticsLayoutRepository.resetWidgetOrder())
    }

    fun addToWatchlist(symbol: String, note: String? = null) {
        repository.addToWatchlist(symbol, note)
        refreshQuotes()
    }

    private fun updateAnalyticsWidgetOrder(order: List<AnalyticsWidgetType>) {
        analyticsLayoutRepository.saveWidgetOrder(order)
        _uiState.update { it.copy(analyticsWidgetOrder = order) }
    }

    private fun swap(order: List<AnalyticsWidgetType>, from: Int, to: Int): List<AnalyticsWidgetType> {
        if (from !in order.indices || to !in order.indices) return order
        val mutable = order.toMutableList()
        val item = mutable.removeAt(from)
        mutable.add(to, item)
        return mutable
    }

    fun removeFromWatchlist(symbol: String) {
        repository.removeFromWatchlist(symbol)
        refreshQuotes()
    }

    fun createAlert(symbol: String, type: AlertType, threshold: Double, note: String = "") {
        alertRepository.createAlert(symbol, type, threshold, note)
        refreshQuotes()
    }

    fun deleteAlert(id: String) {
        alertRepository.deleteAlert(id)
        _uiState.update { state ->
            val alerts = alertRepository.getAlerts()
            state.copy(
                alerts = alerts,
                triggeredAlerts = alerts.filter { it.triggeredAt != null && !it.read },
            )
        }
    }

    fun toggleAlert(id: String, enabled: Boolean) {
        val alert = alertRepository.getAlerts().find { it.id == id } ?: return
        alertRepository.updateAlert(alert.copy(enabled = enabled))
        _uiState.update { it.copy(alerts = alertRepository.getAlerts()) }
    }

    fun clearTriggeredAlert(id: String) {
        alertRepository.clearTriggered(id)
        _uiState.update { state ->
            val alerts = alertRepository.getAlerts()
            state.copy(
                alerts = alerts,
                triggeredAlerts = alerts.filter { it.triggeredAt != null && !it.read },
            )
        }
    }

    fun dismissTriggeredAlerts() {
        val ids = _uiState.value.triggeredAlerts.map { it.id }
        alertRepository.markTriggeredRead(ids)
        _uiState.update { state ->
            val alerts = alertRepository.getAlerts()
            state.copy(
                alerts = alerts,
                triggeredAlerts = alerts.filter { it.triggeredAt != null && !it.read },
            )
        }
    }

    fun updateAvailableCapital(amount: Double) {
        _uiState.update { it.copy(availableCapital = amount) }
        // Potentially refresh scenarios
    }

    companion object {
        private const val POLL_INTERVAL_MS = 30_000L
        private const val CATALOG_REFRESH_EVERY = 5
    }
}
